import type React from 'react';

import { Badge, Link, Text } from '@change/ds-core-ui';
import { ArrowRightIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { usePetitionActivityLink } from './hooks/usePetitionActivityLink';
import type { LinkType } from './types';

const ActivityBadge: React.FC<BadgeVariationProps> = ({ activityCount, variant }) => {
	const { translate } = useI18n();

	const label =
		variant === 'mobile'
			? activityCount
			: `${activityCount} ${translate('corgi.petition_activity.shared.new_activity_badge_text')}`;

	const className = variant === 'mobile' ? 'md:hidden' : 'hidden md:flex';

	return (
		<Badge
			data-testid={`petition-activity-badge-${variant}`}
			label={label}
			size="tiny"
			variant="information"
			emphasis="strong"
			className={className}
		/>
	);
};

/**
 * Renders a link to the petition activity page with an optional badge and text.
 * It currently renders two badges, one for desktop and one for mobile and relies on Tailwind to hide the one that is not needed.
 * It was done this way to avoid having to use the `useIsMobile` hook that's not reactive when the window is resized.
 */
export const PetitionActivityLink: React.FC<Props> = ({ slug, linkType, badgeCount }) => {
	const {
		data: { href, linkTextTranslationKey },
	} = usePetitionActivityLink({ slug, linkType });

	return (
		<Link
			href={href}
			data-testid="petition-activity-link"
			variant="secondary"
			trailingIcon={<ArrowRightIcon size="small" />}
			data-qa="petition-activity-link"
		>
			<div className="flex flex-row items-center gap-2">
				{badgeCount && badgeCount !== '0' && (
					<>
						<ActivityBadge variant="desktop" activityCount={badgeCount} />
						<ActivityBadge variant="mobile" activityCount={badgeCount} />
					</>
				)}
				<Text className="text-size-body-bold sm:hidden md:inline" data-testid="desktop-activity-text">
					<Translate value={`corgi.petition_activity.shared.${linkTextTranslationKey}`} />
				</Text>
				<div className="flex flex-row items-center gap-[3px] md:hidden">
					<Text className="text-size-body-bold" data-testid="mobile-activity-text">
						<Translate value="corgi.petition_activity.shared.all_activity_link_text" />
					</Text>
				</div>
			</div>
		</Link>
	);
};

type Props = {
	slug: string;
	linkType?: LinkType;
	badgeCount?: string;
};

type BadgeVariationProps = {
	variant?: 'mobile' | 'desktop';
	activityCount: string;
};
